<script setup lang="ts">
import { LucideHelpCircle, LucideTrophy, LucideLoader2 } from 'lucide-vue-next'
import type { IQuiz } from './types'

const props = defineProps<{
  quiz: IQuiz
  isLoading: boolean
  hasActiveAttempt: boolean
}>()

const hasHeaderText = computed(() => !!props.quiz.title?.trim() || !!props.quiz.description?.trim())

const emit = defineEmits<{
  start: []
  restart: []
  resume: []
}>()
</script>

<template>
  <div class="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-xl animate-[fadeIn_0.3s_ease-out]">
    <template v-if="quiz.image_url">
      <NuxtImg
        :src="quiz.image_url"
        width="1920"
        height="500"
        :alt="quiz.title"
        class="aspect-[1920/500] w-full object-contain"
        format="avif,webp"
        fetch-priority="high"
      />
      <div v-if="hasHeaderText" class="p-6 text-center">
        <h3 v-if="quiz.title" class="mb-1 text-xl font-bold text-gray-900">{{ quiz.title }}</h3>
        <p v-if="quiz.description" class="text-sm text-gray-800">{{ quiz.description }}</p>
      </div>
    </template>

    <div v-else class="bg-gradient-to-br from-blue-800 to-blue-500 px-6 py-8 text-center text-white">
      <div class="mb-3 text-5xl">🧠</div>
      <h3 v-if="quiz.title" class="mb-1 text-xl font-bold">{{ quiz.title }}</h3>
      <p v-if="quiz.description" class="text-sm text-blue-100">{{ quiz.description }}</p>
    </div>

    <div class="space-y-3 border-t border-gray-100 p-6">
      <div v-if="quiz.total_questions_count" class="flex items-center gap-3 text-sm text-gray-600">
        <span class="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full border border-blue-200 bg-blue-50 text-blue-600">
          <LucideHelpCircle class="h-4 w-4" />
        </span>
        {{ $t('widget.quiz.start.questions_count', quiz.total_questions_count) }}
      </div>
      <div class="flex items-center gap-3 text-sm text-gray-600">
        <span class="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full border border-green-200 bg-green-50 text-green-600">
          <LucideTrophy class="h-4 w-4" />
        </span>
        {{ $t('widget.quiz.start.results_breakdown') }}
      </div>
    </div>

    <div class="border-t border-gray-100 px-6 py-5">
      <div v-if="isLoading" class="flex items-center justify-center py-1">
        <LucideLoader2 class="h-6 w-6 animate-spin text-gray-400" />
      </div>
      <template v-else-if="hasActiveAttempt">
        <p class="mb-3 text-center text-sm text-gray-500">{{ $t('widget.quiz.resume.subtitle') }}</p>
        <div class="flex flex-col gap-2">
          <Button class="w-full" variant="blue" @click="emit('resume')">
            {{ $t('widget.quiz.resume.continue') }}
          </Button>
          <Button class="w-full" variant="outline-gray" @click="emit('restart')">
            {{ $t('widget.quiz.resume.restart') }}
          </Button>
        </div>
      </template>
      <Button v-else class="w-full" variant="blue" @click="emit('start')">
        {{ $t('widget.quiz.start.begin') }}
      </Button>
    </div>
  </div>
</template>
